import React, { useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../../../style.scss";
import "./style.scss";
import { useAppContext } from "../../../../../../AppContext";

function DailySalesSummary(props) {
  const [groupKey, setGroupKey] = useState("");
  const [date, setDate] = useState("");
  const [summaryData, setSummaryData] = useState([]);
  const [rawOutlets, setRawOutlets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);
  const { chimeraserv, user } = useAppContext();

  // CSV Headers - used for both table and CSV export
  const csvHeaders = [
    "storenumber_satellitenumber",
    "MFZ_ID",
    "iso_country_code",
    "business_date",
    "net_sales_amount_breakfast",
    "net_sales_amount_lunch",
    "net_sales_amount_snack",
    "net_sales_amount_dinner",
    "net_sales_amount_evening",
    "net_sales_amount_latenight",
    "traffic_count_breakfast",
    "traffic_count_lunch",
    "traffic_count_snack",
    "traffic_count_dinner",
    "traffic_count_evening",
    "traffic_count_latenight",
    "net_sales_amount_in_restaurant",
    "net_sales_amount_3pd",
    "net_sales_amount_pickup",
    "net_sales_amount_curbside",
    "net_sales_amount_catering",
    "net_sales_amount_directdelivery",
    "net_sales_amount_kiosk",
    "traffic_count_in_restaurant",
    "traffic_count_3pd",
    "traffic_count_pickup",
    "traffic_count_curbside",
    "traffic_count_catering",
    "traffic_count_directdelivery",
    "traffic_count_kiosk",
    "total_unit_count",
    "footlong_count",
    "6inch_count",
    "week_ending_date",
  ];

  // Get today's date in YYYY-MM-DD format as default
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Single helper function to flatten outlet data - used by both table display and CSV export
  const flattenOutletRow = (outlet) => {
    const MFZ_ID_CONSTANT = 88149;
    const deliveryChannels3PD = chimeraserv.Delivery_Channels_3PD.split(
      ","
    ).map((channel) => channel.trim());
    const inRestaurantChannels = chimeraserv.InRestaurant_Channels.split(
      ","
    ).map((channel) => channel.trim());

    const row = {};
    csvHeaders.forEach((header) => {
      if (header === "storenumber_satellitenumber") {
        row[header] = `${outlet.storeNum || ""}`;
      } else if (header === "MFZ_ID") {
        row[header] = MFZ_ID_CONSTANT;
      } else if (header === "iso_country_code") {
        row[header] = outlet.iso_country_code || "";
      } else if (header === "business_date") {
        row[header] = outlet.business_date || "";
      } else if (header === "week_ending_date") {
        row[header] = outlet.week_ending_date || "";
      } else if (header === "net_sales_amount_breakfast") {
        row[header] = outlet.dayparts?.breakfast?.sales ?? 0;
      } else if (header === "net_sales_amount_lunch") {
        row[header] = outlet.dayparts?.lunch?.sales ?? 0;
      } else if (header === "net_sales_amount_snack") {
        row[header] = outlet.dayparts?.snack?.sales ?? 0;
      } else if (header === "net_sales_amount_dinner") {
        row[header] = outlet.dayparts?.dinner?.sales ?? 0;
      } else if (header === "net_sales_amount_evening") {
        row[header] = outlet.dayparts?.evening?.sales ?? 0;
      } else if (header === "net_sales_amount_latenight") {
        row[header] = outlet.dayparts?.latenight?.sales ?? 0;
      } else if (header === "traffic_count_breakfast") {
        row[header] = outlet.dayparts?.breakfast?.traffic ?? 0;
      } else if (header === "traffic_count_lunch") {
        row[header] = outlet.dayparts?.lunch?.traffic ?? 0;
      } else if (header === "traffic_count_snack") {
        row[header] = outlet.dayparts?.snack?.traffic ?? 0;
      } else if (header === "traffic_count_dinner") {
        row[header] = outlet.dayparts?.dinner?.traffic ?? 0;
      } else if (header === "traffic_count_evening") {
        row[header] = outlet.dayparts?.evening?.traffic ?? 0;
      } else if (header === "traffic_count_latenight") {
        row[header] = outlet.dayparts?.latenight?.traffic ?? 0;
      } else if (header === "net_sales_amount_in_restaurant") {
        row[header] = inRestaurantChannels.reduce(
          (sum, channel) =>
            sum + (outlet.sales_channels?.[channel]?.sales ?? 0),
          0
        );
      } else if (header === "net_sales_amount_3pd") {
        row[header] = deliveryChannels3PD.reduce(
          (sum, channel) =>
            sum + (outlet.sales_channels?.[channel]?.sales ?? 0),
          0
        );
      } else if (header === "net_sales_amount_pickup") {
        row[header] = outlet.sales_channels?.["Pickup"]?.sales ?? 0;
      } else if (header === "net_sales_amount_curbside") {
        row[header] = outlet.sales_channels?.["Curbside"]?.sales ?? 0;
      } else if (header === "net_sales_amount_catering") {
        row[header] = outlet.sales_channels?.["Catering"]?.sales ?? 0;
      } else if (header === "net_sales_amount_directdelivery") {
        row[header] = outlet.sales_channels?.["Direct Delivery"]?.sales ?? 0;
      } else if (header === "net_sales_amount_kiosk") {
        row[header] = outlet.sales_channels?.["Kiosk"]?.sales ?? 0;
      } else if (header === "traffic_count_in_restaurant") {
        row[header] = inRestaurantChannels.reduce(
          (sum, channel) =>
            sum + (outlet.sales_channels?.[channel]?.traffic ?? 0),
          0
        );
      } else if (header === "traffic_count_3pd") {
        row[header] = deliveryChannels3PD.reduce(
          (sum, channel) =>
            sum + (outlet.sales_channels?.[channel]?.traffic ?? 0),
          0
        );
      } else if (header === "traffic_count_pickup") {
        row[header] = outlet.sales_channels?.["Pickup"]?.traffic ?? 0;
      } else if (header === "traffic_count_curbside") {
        row[header] = outlet.sales_channels?.["Curbside"]?.traffic ?? 0;
      } else if (header === "traffic_count_catering") {
        row[header] = outlet.sales_channels?.["Catering"]?.traffic ?? 0;
      } else if (header === "traffic_count_directdelivery") {
        row[header] = outlet.sales_channels?.["Direct Delivery"]?.traffic ?? 0;
      } else if (header === "traffic_count_kiosk") {
        row[header] = outlet.sales_channels?.["Kiosk"]?.traffic ?? 0;
      } else if (header === "total_unit_count") {
        row[header] = outlet.total_unit_count ?? 0;
      } else if (header === "footlong_count") {
        row[header] = outlet.footlong_count ?? 0;
      } else if (header === "6inch_count") {
        row[header] = outlet["6inch_count"] ?? 0;
      } else {
        row[header] = outlet[header] ?? 0;
      }
    });
    return row;
  };

  // Wrapper for table display
  const flattenForCSVAndTable = (outlet) => flattenOutletRow(outlet);

  const getIntegrations = async () => {
    const authToken = user?.data?.usrKey || chimeraserv.authToken;

    try {
      const fetchResponse = await axios({
        method: "GET",
        url: `${chimeraserv.urlGet}/integration/list`,
        headers: {
          Authorization: authToken,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      return fetchResponse.data || [];
    } catch (err) {
      console.error(`Error fetching integrations: ${err.message}`);
      return [];
    }
  };

  const getStoreNumberFromIntegration = (
    outlets,
    storenumberSatelliteNumber,
    integrations
  ) => {
    const subwayIntegration = integrations.find(
      (intg) => intg.braKey === `${chimeraserv.subway_brakey}`
    );

    if (Array.isArray(subwayIntegration?.outlets)) {
      const found = subwayIntegration.outlets.find(
        (o) => o.chaKey === storenumberSatelliteNumber
      );
      if (found) {
        return found.storeNum || "";
      }
    }
    return "";
  };
  // Generate CSV from the fetched data
  const generateCSV = (outlets) => {
    if (!outlets || outlets.length === 0) {
      return "";
    }

    // Create CSV content using csvHeaders constant
    let csv = csvHeaders.map((h) => `"${h}"`).join(",") + "\n";

    // Add data rows
    outlets.forEach((outlet) => {
      const flattenedRow = flattenOutletRow(outlet);
      const row = csvHeaders.map((header) => {
        let value = flattenedRow[header];

        // Escape quotes in values and convert to string
        value = String(value).replace(/"/g, '""');
        return `"${value}"`;
      });

      csv += row.join(",") + "\n";
    });

    return csv;
  };

  const handleFetchSummary = async (e) => {
    e.preventDefault();
    // Fetch integrations for store number lookup
    const integrations = await getIntegrations();

    if (!groupKey.trim()) {
      setError("Please enter a Group Key");
      return;
    }

    if (!date) {
      setError("Please select a date");
      return;
    }

    // Validate date format
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setError("Invalid date format. Use YYYY-MM-DD");
      return;
    }

    setLoading(true);
    setError(null);
    setSummaryData([]);
    setRawOutlets([]);
    setDataFetched(false);

    try {
      // Get authorization token from user data if available
      const authToken = user?.data?.usrKey || chimeraserv.authToken;

      // Updated endpoint format: /api/daily-sales-summary/:grpKey?date=YYYY-MM-DD
      const response = await axios({
        method: "GET",
        url: `${chimeraserv.urlInsightBackendReading}/api/repush/daily-sales-summary/${groupKey}?date=${date}`,
        headers: {
          Authorization: authToken,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (response.data.error) {
        setError(response.data.error);
        setSummaryData([]);
        setRawOutlets([]);
      } else {
        // Extract outlets from response.data.days which is an array of day objects
        let outlets = [];

        const extractFromDaysArray = (daysArray) =>
          daysArray.flatMap((day) =>
            Array.isArray(day.outlets)
              ? day.outlets.map((outlet) => ({
                  ...outlet,
                  business_date:
                    outlet.business_date || day.date || day.business_date || "",
                  week_ending_date:
                    outlet.week_ending_date || day.week_ending_date || "",
                }))
              : []
          );

        if (response.data.data && Array.isArray(response.data.data.days)) {
          outlets = extractFromDaysArray(response.data.data.days);
        } else if (Array.isArray(response.data.days)) {
          outlets = extractFromDaysArray(response.data.days);
        } else if (Array.isArray(response.data)) {
          // in some cases response.data itself may be the days array
          outlets = extractFromDaysArray(response.data);
        } else if (Array.isArray(response.data.outlets)) {
          outlets = response.data.outlets;
        }

        // Store raw outlets for CSV generation
        setRawOutlets(outlets);

        // Enrich outlets with store numbers
        const enrichedOutlets = outlets.map((outlet) => ({
          ...outlet,
          storeNum: getStoreNumberFromIntegration(
            outlets,
            outlet.storenumber_satellitenumber,
            integrations
          ),
        }));

        // Flatten the outlet data for table display using CSV format
        const flattenedData = enrichedOutlets.map((outlet) =>
          flattenForCSVAndTable(outlet)
        );
        setSummaryData(flattenedData);
        setDataFetched(true);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch daily sales summary";
      setError(errorMessage);
      setSummaryData([]);
      setRawOutlets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    const integrations = await getIntegrations(); // Fetch integrations for store number lookup
    if (rawOutlets.length === 0) {
      setError("No data available for CSV export");
      return;
    }

    // Enrich outlets with store numbers
    const enrichedOutlets = rawOutlets.map((outlet) => ({
      ...outlet,
      storeNum: getStoreNumberFromIntegration(
        rawOutlets,
        outlet.storenumber_satellitenumber,
        integrations
      ),
    }));

    try {
      const csvContent = generateCSV(enrichedOutlets);

      // Create blob and trigger download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const yyyymmdd = `${yyyy}${mm}${dd}`;

      // Format date as YYYYMMDD
      //const formattedDate = (date || getTodayDate()).replace(/-/g, "");
      link.setAttribute(
        "download",
        `${chimeraserv.mfzId}_dailysalessummary_${yyyymmdd}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      setError("Failed to generate CSV: " + error.message);
    }
  };

  const handleRepushToS3 = async () => {
    if (rawOutlets.length === 0) {
      setError("No data available to push to S3");
      return;
    }
    setError(null);
    setLoading(true);

    try {
      const authToken = user?.data?.usrKey || chimeraserv.authToken;

      const response = await axios.post(
        `${chimeraserv.urlAwsPusher}/upload/daily-sales-summary`,
        { data: rawOutlets },
        {
          headers: {
            Authorization: authToken,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      if (response.data && response.data.success) {
        setError(null);
        alert(
          "Successfully pushed daily sales to S3.\nS3 URL: " + response.data.url
        );
      } else {
        setError(response.data.error || "Failed to push to S3");
      }
    } catch (error) {
      setError(
        error.response?.data?.error ||
          error.message ||
          "Failed to push daily sales to S3"
      );
    } finally {
      setLoading(false);
    }
  };

  const getTableColumns = () => csvHeaders;

  const columns = getTableColumns();
  const isArrayData = Array.isArray(summaryData) && summaryData.length > 0;

  // Format cell value for display
  const formatCellValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "number") {
      return Number.isInteger(value)
        ? value.toLocaleString()
        : value.toFixed(2).toLocaleString();
    }
    if (typeof value === "object") {
      return JSON.stringify(value);
    }
    return String(value);
  };

  // Format column header name
  const formatColumnName = (columnName) => {
    return columnName
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="daily-sales-summary-container">
      <div className="viewContainer">
        <h2 className="viewTitle">Daily Sales Summary</h2>

        <div className="sales-summary-form">
          <form onSubmit={handleFetchSummary}>
            <div className="form-group">
              <label htmlFor="groupKey">Branch Key</label>
              <input
                type="text"
                id="groupKey"
                className="form-control"
                placeholder="e.g., eyJuYW1lIjoiQ0hTIERlbW8ifQ"
                value={groupKey}
                onChange={(e) => setGroupKey(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="date">Date</label>
              <input
                type="date"
                id="date"
                className="form-control"
                value={date || getTodayDate()}
                onChange={(e) => setDate(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                <FontAwesomeIcon icon={["fas", "search"]} />
                {loading ? " Fetching..." : " Get Daily Sales"}
              </button>

              {dataFetched && isArrayData && (
                <>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleDownloadCSV}
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon={["fas", "download"]} />
                    CSV
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{ marginLeft: "10px" }}
                    onClick={handleRepushToS3}
                    disabled={loading || rawOutlets.length === 0}
                  >
                    <FontAwesomeIcon icon={["fas", "cloud-upload-alt"]} />
                    Repush
                  </button>
                </>
              )}
            </div>
          </form>
        </div>

        {error && (
          <div className="alert alert-danger">
            <FontAwesomeIcon icon={["fas", "exclamation-circle"]} />
            {error}
          </div>
        )}

        {loading && (
          <div className="loading-container">
            <div className="spinner">
              <FontAwesomeIcon icon={["fas", "spinner"]} spin />
            </div>
            <p>Fetching daily sales summary...</p>
          </div>
        )}

        {!loading && dataFetched && isArrayData && (
          <div className="sales-summary-table-wrapper">
            <h3>Sales Summary for {date}</h3>
            <div className="table-scroll-wrapper">
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    {columns.map((column) => (
                      <th key={column} title={column}>
                        {formatColumnName(column)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {summaryData.map((row, index) => (
                    <tr key={index}>
                      {columns.map((column) => (
                        <td key={`${index}-${column}`}>
                          {formatCellValue(row[column])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="summary-info">
              <p>Total Outlets: {summaryData.length}</p>
            </div>
          </div>
        )}

        {!loading && dataFetched && !isArrayData && (
          <div className="no-data-message">
            <FontAwesomeIcon icon={["fas", "info-circle"]} />
            No sales data available for the selected date and group.
          </div>
        )}

        {!dataFetched && !loading && (
          <div className="placeholder-message">
            <FontAwesomeIcon icon={["fas", "chart-bar"]} />
            Enter Group Key and select a date to fetch daily sales summary.
          </div>
        )}
      </div>
    </div>
  );
}

export default DailySalesSummary;
